import AppsRoundedIcon from "@mui/icons-material/AppsRounded";
import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DevicesOutlinedIcon from "@mui/icons-material/DevicesOutlined";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import RouterOutlinedIcon from "@mui/icons-material/RouterOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import WifiOutlinedIcon from "@mui/icons-material/WifiOutlined";

import {
  Alert,
  Box,
  Chip,
  IconButton,
  Paper,
  Stack,
  Tooltip as MuiTooltip,
  Typography,
} from "@mui/material";

import {
  useQueries,
  useQuery,
} from "@tanstack/react-query";

import {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { BrandSpinner } from "../../../components/feedback/BrandSpinner";
import { SoftDataGrid } from "../../../components/data/SoftDataGrid";

import { getAllAnalyticsShops } from "../../../services/trafficAnalyticsShopService";

import {
  getTrafficApplications,
  getTrafficCategories,
  getTrafficCountries,
  getTrafficEvents,
  getTrafficFirewalls,
  getTrafficOverview,
  getTrafficShops,
  getTrafficTimeSeries,
} from "../../../services/trafficAnalyticsService";

import { getMonthToDateRange } from "../../../utils/localDate";

import { TrafficAnalyticsFiltersDrawer } from "../components/TrafficAnalyticsFiltersDrawer";

const chartColors = [
  "#FF8A65",
  "#4DB6AC",
  "#64B5F6",
  "#BA68C8",
  "#FFD54F",
  "#81C784",
  "#F06292",
  "#7986CB",
];

function numeric(value) {
  return Number(value || 0);
}

function formatBytes(bytes = 0) {
  const value =
    Number(bytes || 0);

  if (
    value >=
    1024 * 1024 * 1024 * 1024
  ) {
    return `${(
      value /
      1024 /
      1024 /
      1024 /
      1024
    ).toFixed(2)} TB`;
  }

  if (
    value >=
    1024 * 1024 * 1024
  ) {
    return `${(
      value /
      1024 /
      1024 /
      1024
    ).toFixed(2)} GB`;
  }

  if (
    value >=
    1024 * 1024
  ) {
    return `${(
      value /
      1024 /
      1024
    ).toFixed(2)} MB`;
  }

  if (value >= 1024) {
    return `${(
      value / 1024
    ).toFixed(2)} KB`;
  }

  return `${value} bytes`;
}

function formatDateTime(value) {
  if (!value) {
    return "—";
  }

  return new Date(
    value,
  ).toLocaleString(
    "en-ZA",
    {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    },
  );
}

function formatPeriod(value) {
  if (!value) {
    return "";
  }

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime(),
    )
  ) {
    return value;
  }

  return date.toLocaleDateString(
    "en-ZA",
    {
      day: "2-digit",
      month: "short",
    },
  );
}

function comparisonText(value) {
  if (
    value === null ||
    value === undefined
  ) {
    return "No previous period baseline";
  }

  const amount =
    Number(value);

  if (amount === 0) {
    return "No change from previous period";
  }

  if (amount > 0) {
    return `+${amount.toFixed(
      2,
    )}% vs previous period`;
  }

  return `${amount.toFixed(
    2,
  )}% vs previous period`;
}

function KpiCard({
  label,
  value,
  description,
  icon,
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        minHeight: 112,
        borderRadius: 0,
        bgcolor: "#FFFFFF",
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <Box>
          <Typography
            sx={{
              color: "#667085",
              fontSize: "0.66rem",
              fontWeight: 750,
            }}
          >
            {label}
          </Typography>

          <Typography
            sx={{
              mt: 0.45,
              color: "#101828",
              fontSize: "1.45rem",
              fontWeight: 900,
              lineHeight: 1.15,
            }}
          >
            {value}
          </Typography>

          <Typography
            sx={{
              mt: 0.55,
              color: "#98A2B3",
              fontSize: "0.61rem",
            }}
          >
            {description ||
              "\u00A0"}
          </Typography>
        </Box>

        <Box
          sx={{
            width: 36,
            height: 36,
            display: "grid",
            placeItems: "center",
            bgcolor: "#F2F4F7",
            border:
              "1px solid #EAECF0",
          }}
        >
          {icon}
        </Box>
      </Stack>
    </Paper>
  );
}

function SectionHeader({
  title,
  subtitle,
}) {
  return (
    <Box>
      <Typography
        sx={{
          color: "#101828",
          fontSize: "0.8rem",
          fontWeight: 850,
        }}
      >
        {title}
      </Typography>

      {subtitle && (
        <Typography
          sx={{
            mt: 0.15,
            color: "#667085",
            fontSize: "0.62rem",
          }}
        >
          {subtitle}
        </Typography>
      )}
    </Box>
  );
}

export function TrafficAnalyticsPage() {
  const [
    drawerOpen,
    setDrawerOpen,
  ] = useState(false);

  const [
    filters,
    setFilters,
  ] = useState(() => ({
    ...getMonthToDateRange(),

    shop_ids: [],

    interval: "day",
  }));

  /*
   * Shop selector.
   *
   * getAllAnalyticsShops()
   * automatically follows the
   * paginated shop endpoint
   * using max 100 per page.
   */
  const shopsQuery =
    useQuery({
      queryKey: [
        "wifi-analytics-shop-options",
      ],

      queryFn: () =>
        getAllAnalyticsShops({
          is_active: 1,
        }),

      staleTime:
        5 * 60 * 1000,
    });

  const shops =
    useMemo(
      () =>
        Array.isArray(
          shopsQuery.data,
        )
          ? shopsQuery.data
          : [],
      [shopsQuery.data],
    );

  /*
   * Default to every visible
   * shop when the page loads.
   */
  useEffect(() => {
    if (
      shops.length > 0 &&
      filters.shop_ids.length === 0
    ) {
      setFilters(
        (current) => ({
          ...current,

          shop_ids:
            shops.map(
              (shop) =>
                shop.id,
            ),
        }),
      );
    }
  }, [
    shops,
    filters.shop_ids.length,
  ]);

  const commonFilters =
    useMemo(
      () => ({
        shop_ids:
          filters.shop_ids,

        date_from:
          filters.date_from,

        date_to:
          filters.date_to,
      }),
      [
        filters.shop_ids,
        filters.date_from,
        filters.date_to,
      ],
    );

  const timeSeriesFilters =
    useMemo(
      () => ({
        ...commonFilters,

        interval:
          filters.interval,
      }),
      [
        commonFilters,
        filters.interval,
      ],
    );

  const analyticsQueries =
    useQueries({
      queries: [
        {
          queryKey: [
            "wifi-traffic-overview",
            commonFilters,
          ],

          queryFn: () =>
            getTrafficOverview(
              commonFilters,
            ),

          enabled:
            filters.shop_ids
              .length > 0,

          refetchInterval:
            60000,
        },

        {
          queryKey: [
            "wifi-traffic-series",
            timeSeriesFilters,
          ],

          queryFn: () =>
            getTrafficTimeSeries(
              timeSeriesFilters,
            ),

          enabled:
            filters.shop_ids
              .length > 0,

          refetchInterval:
            60000,
        },

        {
          queryKey: [
            "wifi-traffic-shops",
            commonFilters,
          ],

          queryFn: () =>
            getTrafficShops({
              ...commonFilters,

              sort_by:
                "total_bytes",

              sort_direction:
                "desc",

              limit: 100,
            }),

          enabled:
            filters.shop_ids
              .length > 0,

          refetchInterval:
            60000,
        },

        {
          queryKey: [
            "wifi-traffic-applications",
            commonFilters,
          ],

          queryFn: () =>
            getTrafficApplications({
              ...commonFilters,
              limit: 12,
            }),

          enabled:
            filters.shop_ids
              .length > 0,
        },

        {
          queryKey: [
            "wifi-traffic-categories",
            commonFilters,
          ],

          queryFn: () =>
            getTrafficCategories({
              ...commonFilters,
              limit: 10,
            }),

          enabled:
            filters.shop_ids
              .length > 0,
        },

        {
          queryKey: [
            "wifi-traffic-countries",
            commonFilters,
          ],

          queryFn: () =>
            getTrafficCountries({
              ...commonFilters,
              limit: 12,
            }),

          enabled:
            filters.shop_ids
              .length > 0,
        },

        {
          queryKey: [
            "wifi-traffic-firewalls",
            commonFilters,
          ],

          queryFn: () =>
            getTrafficFirewalls(
              commonFilters,
            ),

          enabled:
            filters.shop_ids
              .length > 0,
        },

        {
          queryKey: [
            "wifi-traffic-events",
            commonFilters,
          ],

          queryFn: () =>
            getTrafficEvents({
              ...commonFilters,
              per_page: 25,
            }),

          enabled:
            filters.shop_ids
              .length > 0,

          refetchInterval:
            60000,
        },
      ],
    });

  const [
    overviewQuery,
    seriesQuery,
    shopsUsageQuery,
    applicationsQuery,
    categoriesQuery,
    countriesQuery,
    firewallsQuery,
    eventsQuery,
  ] = analyticsQueries;

  const overview =
    overviewQuery.data
      ?.summary || {};

  const comparison =
    overviewQuery.data
      ?.comparison || {};

  const series =
    seriesQuery.data
      ?.series || [];

  const shopUsage =
    shopsUsageQuery.data
      ?.shops || [];

  const applications =
    applicationsQuery.data
      ?.applications || [];

  const categories =
    categoriesQuery.data
      ?.categories || [];

  const countries =
    countriesQuery.data
      ?.countries || [];

  const firewalls =
    firewallsQuery.data
      ?.firewalls || [];

  const events =
    eventsQuery.data
      ?.events?.data || [];

  const trendData =
    useMemo(
      () =>
        series.map(
          (row) => ({
            period:
              row.period,

            label:
              formatPeriod(
                row.period,
              ),

            upload_bytes:
              numeric(
                row.upload?.bytes,
              ),

            download_bytes:
              numeric(
                row.download?.bytes,
              ),

            total_bytes:
              numeric(
                row.total?.bytes,
              ),

            voucher_count:
              numeric(
                row.voucher_count,
              ),

            event_count:
              numeric(
                row.event_count,
              ),
          }),
        ),
      [series],
    );

  const shopChartData =
    useMemo(
      () =>
        shopUsage
          .slice(0, 15)
          .map(
            (row) => ({
              ...row,

              shop_label:
                row.shop
                  ?.shop_code ||
                row.shop
                  ?.shop_name ||
                "Unknown",

              upload_bytes:
                numeric(
                  row.upload
                    ?.bytes,
                ),

              download_bytes:
                numeric(
                  row.download
                    ?.bytes,
                ),

              total_bytes:
                numeric(
                  row.total
                    ?.bytes,
                ),
            }),
          ),
      [shopUsage],
    );

  const categoryChartData =
    useMemo(
      () =>
        categories.map(
          (row) => ({
            ...row,

            total_bytes:
              numeric(
                row.total
                  ?.bytes,
              ),
          }),
        ),
      [categories],
    );

  const applicationChartData =
    useMemo(
      () =>
        applications.map(
          (row) => ({
            ...row,

            total_bytes:
              numeric(
                row.total
                  ?.bytes,
              ),
          }),
        ),
      [applications],
    );

  const countryChartData =
    useMemo(
      () =>
        countries.map(
          (row) => ({
            ...row,

            total_bytes:
              numeric(
                row.total
                  ?.bytes,
              ),
          }),
        ),
      [countries],
    );

  const shopColumns =
    useMemo(
      () => [
        {
          field: "shop_name",

          headerName: "Shop",

          minWidth: 230,

          flex: 1,

          valueGetter: (
            _,
            row,
          ) =>
            row.shop
              ?.shop_name ||
            "—",

          renderCell: (
            params,
          ) => (
            <Box>
              <Typography
                sx={{
                  fontSize:
                    "0.69rem",

                  fontWeight:
                    850,
                }}
              >
                {params.row
                  .shop
                  ?.shop_name ||
                  "—"}
              </Typography>

              <Typography
                sx={{
                  color:
                    "#667085",

                  fontSize:
                    "0.6rem",
                }}
              >
                {params.row
                  .shop
                  ?.shop_code ||
                  "—"}

                {params.row
                  .shop
                  ?.brand
                  ?.name
                  ? ` • ${params.row.shop.brand.name}`
                  : ""}
              </Typography>
            </Box>
          ),
        },

        {
          field:
            "voucher_count",

          headerName:
            "Vouchers",

          width: 100,
        },

        {
          field:
            "unique_device_count",

          headerName:
            "Devices",

          width: 100,
        },

        {
          field:
            "event_count",

          headerName:
            "Events",

          width: 90,
        },

        {
          field:
            "upload_display",

          headerName:
            "Upload",

          minWidth: 115,

          valueGetter: (
            _,
            row,
          ) =>
            row.upload
              ?.human ||
            "0 bytes",
        },

        {
          field:
            "download_display",

          headerName:
            "Download",

          minWidth: 115,

          valueGetter: (
            _,
            row,
          ) =>
            row.download
              ?.human ||
            "0 bytes",
        },

        {
          field:
            "total_display",

          headerName:
            "Total usage",

          minWidth: 120,

          valueGetter: (
            _,
            row,
          ) =>
            row.total
              ?.human ||
            "0 bytes",
        },

        {
          field:
            "average_usage",

          headerName:
            "Avg / voucher",

          minWidth: 125,

          valueGetter: (
            _,
            row,
          ) =>
            row
              .average_usage_per_voucher
              ?.human ||
            "0 bytes",
        },

        {
          field:
            "share_percentage",

          headerName:
            "Share",

          width: 90,

          valueFormatter: (
            value,
          ) =>
            `${numeric(
              value,
            ).toFixed(2)}%`,
        },
      ],
      [],
    );

  const eventColumns =
    useMemo(
      () => [
        {
          field:
            "shop_name",

          headerName:
            "Shop",

          minWidth: 190,

          flex: 1,

          valueGetter: (
            _,
            row,
          ) =>
            row.shop
              ?.shop_name ||
            "—",

          renderCell: (
            params,
          ) => (
            <Box>
              <Typography
                sx={{
                  fontSize:
                    "0.68rem",

                  fontWeight:
                    850,
                }}
              >
                {params.row
                  .shop
                  ?.shop_name ||
                  "—"}
              </Typography>

              <Typography
                sx={{
                  color:
                    "#667085",

                  fontSize:
                    "0.6rem",
                }}
              >
                {params.row
                  .shop
                  ?.shop_code ||
                  "—"}
              </Typography>
            </Box>
          ),
        },

        {
          field:
            "username",

          headerName:
            "Voucher",

          minWidth: 190,

          flex: 1,
        },

        {
          field:
            "application_name",

          headerName:
            "Application",

          minWidth: 180,

          flex: 1,

          valueGetter: (
            _,
            row,
          ) =>
            row.application_name ||
            "Unknown",
        },

        {
          field:
            "application_category",

          headerName:
            "Category",

          minWidth: 170,

          valueGetter: (
            _,
            row,
          ) =>
            row.application_category ||
            "Unknown",
        },

        {
          field:
            "source_ip",

          headerName:
            "Client IP",

          width: 135,

          valueGetter: (
            _,
            row,
          ) =>
            row.source_ip ||
            "—",
        },

        {
          field:
            "source_mac",

          headerName:
            "MAC address",

          width: 155,

          valueGetter: (
            _,
            row,
          ) =>
            row.source_mac ||
            "—",
        },

        {
          field:
            "upload_display",

          headerName:
            "Upload",

          minWidth: 110,

          valueGetter: (
            _,
            row,
          ) =>
            row.upload
              ?.human ||
            formatBytes(
              row.upload_bytes,
            ),
        },

        {
          field:
            "download_display",

          headerName:
            "Download",

          minWidth: 110,

          valueGetter: (
            _,
            row,
          ) =>
            row.download
              ?.human ||
            formatBytes(
              row.download_bytes,
            ),
        },

        {
          field:
            "total_display",

          headerName:
            "Total",

          minWidth: 110,

          valueGetter: (
            _,
            row,
          ) =>
            row.total
              ?.human ||
            formatBytes(
              row.total_bytes,
            ),
        },

        {
          field:
            "destination_country",

          headerName:
            "Country",

          width: 95,

          valueGetter: (
            _,
            row,
          ) =>
            row.destination_country ||
            "—",
        },

        {
          field:
            "event_at",

          headerName:
            "Event time",

          minWidth: 180,

          valueFormatter: (
            value,
          ) =>
            formatDateTime(
              value,
            ),
        },
      ],
      [],
    );

  const isLoading =
    shopsQuery.isLoading ||
    overviewQuery.isLoading ||
    seriesQuery.isLoading;

  const hasError =
    shopsQuery.isError ||
    analyticsQueries.some(
      (query) =>
        query.isError,
    );

  const allSelected =
    shops.length > 0 &&
    filters.shop_ids.length ===
      shops.length;

  const selectedText =
    allSelected
      ? "All shops"
      : `${filters.shop_ids.length} ${
          filters.shop_ids.length ===
          1
            ? "shop"
            : "shops"
        }`;

  function refreshAll() {
    analyticsQueries.forEach(
      (query) => {
        query.refetch();
      },
    );
  }

  function resetFilters() {
    setFilters({
      ...getMonthToDateRange(),

      shop_ids:
        shops.map(
          (shop) =>
            shop.id,
        ),

      interval: "day",
    });
  }

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: 420,

          display: "grid",

          placeItems: "center",
        }}
      >
        <BrandSpinner />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: {
          xs: 1.2,
          md: 2,
        },

        bgcolor: "#F7F8FA",

        minHeight: "100%",
      }}
    >
      {/* Header */}

      <Stack
        direction={{
          xs: "column",
          sm: "row",
        }}
        justifyContent="space-between"
        alignItems={{
          xs: "stretch",
          sm: "center",
        }}
        spacing={1}
        sx={{ mb: 1.2 }}
      >
        <Box>
          {/* <Typography
            sx={{
              fontSize: "1.08rem",
              fontWeight: 900,
              color: "#101828",
            }}
          >
            Guest Wi-Fi analytics
          </Typography> */}

          {/* <Typography
            sx={{
              mt: 0.2,
              color: "#667085",
              fontSize: "0.67rem",
            }}
          >
            {selectedText}
            {" • "}
            {filters.date_from}
            {" → "}
            {filters.date_to}
          </Typography> */}
        </Box>

        <Stack
          direction="row"
          spacing={0.5}
          justifyContent="flex-end"
        >
          {/* <Chip
            label={selectedText}
            size="small"
            variant="outlined"
            sx={{
              borderRadius: 0,
              bgcolor: "#FFFFFF",
            }}
          /> */}

          <MuiTooltip title="Refresh analytics">
            <IconButton
              size="small"
              onClick={refreshAll}
              sx={{
                border:
                  "1px solid #DDE3EA",

                borderRadius: 0,

                bgcolor:
                  "#FFFFFF",
              }}
            >
              <RefreshRoundedIcon
                sx={{
                  fontSize: 18,
                }}
              />
            </IconButton>
          </MuiTooltip>

          <MuiTooltip title="Analytics filters">
            <IconButton
              size="small"
              onClick={() =>
                setDrawerOpen(true)
              }
              sx={{
                border:
                  "1px solid #DDE3EA",

                borderRadius: 0,

                bgcolor:
                  "#FFFFFF",
              }}
            >
              <FilterAltOutlinedIcon
                sx={{
                  fontSize: 18,
                }}
              />
            </IconButton>
          </MuiTooltip>
        </Stack>
      </Stack>

      {hasError && (
        <Alert
          severity="warning"
          sx={{
            mb: 1.2,
            borderRadius: 0,
          }}
        >
          Some analytics data could
          not be loaded.
        </Alert>
      )}

      {/* KPI cards */}

      <Box
        sx={{
          display: "grid",

          gridTemplateColumns: {
            xs: "1fr",

            sm:
              "repeat(2, 1fr)",

            lg:
              "repeat(4, 1fr)",
          },

          gap: 1.2,
        }}
      >
        <KpiCard
          label="Total data used"
          value={
            overview.total
              ?.human ||
            "0 bytes"
          }
          description={comparisonText(
            comparison
              .total_bytes_change_percentage,
          )}
          icon={
            <WifiOutlinedIcon
              sx={{
                fontSize: 19,
              }}
            />
          }
        />

        <KpiCard
          label="Download"
          value={
            overview.download
              ?.human ||
            "0 bytes"
          }
          description={`${numeric(
            overview.download_percentage,
          ).toFixed(1)}% of traffic`}
          icon={
            <CloudDownloadOutlinedIcon
              sx={{
                fontSize: 19,
              }}
            />
          }
        />

        <KpiCard
          label="Upload"
          value={
            overview.upload
              ?.human ||
            "0 bytes"
          }
          description={`${numeric(
            overview.upload_percentage,
          ).toFixed(1)}% of traffic`}
          icon={
            <CloudUploadOutlinedIcon
              sx={{
                fontSize: 19,
              }}
            />
          }
        />

        <KpiCard
          label="Vouchers used"
          value={
            overview.voucher_count ||
            0
          }
          description={`${numeric(
            overview.average_mb_per_voucher,
          ).toFixed(2)} MB average`}
          icon={
            <LocalOfferOutlinedIcon
              sx={{
                fontSize: 19,
              }}
            />
          }
        />

        <KpiCard
          label="Shops with traffic"
          value={
            overview.shop_count ||
            0
          }
          description={`${numeric(
            overview.average_gb_per_shop,
          ).toFixed(3)} GB average`}
          icon={
            <StorefrontOutlinedIcon
              sx={{
                fontSize: 19,
              }}
            />
          }
        />

        <KpiCard
          label="Unique devices"
          value={
            overview.unique_device_count ||
            0
          }
          description={`${overview.unique_client_count || 0} client IPs`}
          icon={
            <DevicesOutlinedIcon
              sx={{
                fontSize: 19,
              }}
            />
          }
        />

        <KpiCard
          label="Applications"
          value={
            overview.application_count ||
            0
          }
          description="Detected applications"
          icon={
            <AppsRoundedIcon
              sx={{
                fontSize: 19,
              }}
            />
          }
        />

        <KpiCard
          label="Connection duration"
          value={`${numeric(
            overview.duration_hours,
          ).toFixed(2)} hrs`}
          description={`${overview.event_count || 0} traffic events`}
          icon={
            <RouterOutlinedIcon
              sx={{
                fontSize: 19,
              }}
            />
          }
        />
      </Box>

      {/* Usage movement */}

      <Paper
        variant="outlined"
        sx={{
          p: 1.5,

          mt: 1.2,

          minHeight: 395,

          borderRadius: 0,
        }}
      >
        <SectionHeader
          title="Data usage movement"
          subtitle="Upload, download and total traffic over the selected period."
        />

        <Box
          sx={{
            mt: 1,

            height: 325,
          }}
        >
          <ResponsiveContainer
            width="100%"
            height="100%"
          >
            <LineChart
              data={trendData}
              margin={{
                top: 10,
                right: 25,
                bottom: 10,
                left: 20,
              }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#EAECF0"
              />

              <XAxis
                dataKey="label"
                tick={{
                  fontSize: 10,
                }}
              />

              <YAxis
                tick={{
                  fontSize: 10,
                }}
                tickFormatter={
                  formatBytes
                }
              />

              <Tooltip
                formatter={(
                  value,
                  name,
                ) => [
                  formatBytes(
                    value,
                  ),
                  name,
                ]}
              />

              <Legend />

              <Line
                type="monotone"
                dataKey="upload_bytes"
                name="Upload"
                stroke={
                  chartColors[0]
                }
                strokeWidth={2}
                dot={false}
              />

              <Line
                type="monotone"
                dataKey="download_bytes"
                name="Download"
                stroke={
                  chartColors[1]
                }
                strokeWidth={2}
                dot={false}
              />

              <Line
                type="monotone"
                dataKey="total_bytes"
                name="Total"
                stroke={
                  chartColors[2]
                }
                strokeWidth={3}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </Paper>

      {/* Shop comparison/category */}

      <Box
        sx={{
          display: "grid",

          gridTemplateColumns: {
            xs: "1fr",

            xl:
              "1.55fr 1fr",
          },

          gap: 1.2,

          mt: 1.2,
        }}
      >
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,

            minHeight: 420,

            borderRadius: 0,
          }}
        >
          <SectionHeader
            title="Shop data usage comparison"
            subtitle="Compare upload and download traffic across the selected shops."
          />

          <Box
            sx={{
              mt: 1,

              height: 350,
            }}
          >
            <ResponsiveContainer
              width="100%"
              height="100%"
            >
              <BarChart
                data={shopChartData}
                layout="vertical"
                margin={{
                  top: 5,
                  right: 30,
                  bottom: 5,
                  left: 15,
                }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#EAECF0"
                />

                <XAxis
                  type="number"
                  tickFormatter={
                    formatBytes
                  }
                  tick={{
                    fontSize: 10,
                  }}
                />

                <YAxis
                  type="category"
                  dataKey="shop_label"
                  width={90}
                  tick={{
                    fontSize: 10,
                  }}
                />

                <Tooltip
                  formatter={(
                    value,
                  ) =>
                    formatBytes(
                      value,
                    )
                  }
                />

                <Legend />

                <Bar
                  dataKey="upload_bytes"
                  name="Upload"
                  stackId="traffic"
                  fill={
                    chartColors[0]
                  }
                />

                <Bar
                  dataKey="download_bytes"
                  name="Download"
                  stackId="traffic"
                  fill={
                    chartColors[1]
                  }
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Paper>

        <Paper
          variant="outlined"
          sx={{
            p: 1.5,

            minHeight: 420,

            borderRadius: 0,
          }}
        >
          <SectionHeader
            title="Traffic categories"
            subtitle="Usage distribution by Sophos application category."
          />

          <Box
            sx={{
              mt: 1,

              height: 350,
            }}
          >
            <ResponsiveContainer
              width="100%"
              height="100%"
            >
              <PieChart>
                <Pie
                  data={
                    categoryChartData
                  }
                  dataKey="total_bytes"
                  nameKey="name"
                  innerRadius={65}
                  outerRadius={105}
                  paddingAngle={2}
                >
                  {categoryChartData.map(
                    (
                      item,
                      index,
                    ) => (
                      <Cell
                        key={`${item.name}-${index}`}
                        fill={
                          chartColors[
                            index %
                              chartColors.length
                          ]
                        }
                      />
                    ),
                  )}
                </Pie>

                <Tooltip
                  formatter={(
                    value,
                  ) =>
                    formatBytes(
                      value,
                    )
                  }
                />

                <Legend
                  wrapperStyle={{
                    fontSize:
                      "10px",
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
      </Box>

      {/* Applications/Countries */}

      <Box
        sx={{
          display: "grid",

          gridTemplateColumns: {
            xs: "1fr",
            lg: "1fr 1fr",
          },

          gap: 1.2,

          mt: 1.2,
        }}
      >
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            minHeight: 390,
            borderRadius: 0,
          }}
        >
          <SectionHeader
            title="Top applications"
            subtitle="Applications responsible for the largest share of usage."
          />

          <Box
            sx={{
              mt: 1,
              height: 315,
            }}
          >
            <ResponsiveContainer
              width="100%"
              height="100%"
            >
              <BarChart
                data={
                  applicationChartData
                }
                margin={{
                  top: 5,
                  right: 20,
                  bottom: 70,
                  left: 15,
                }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#EAECF0"
                />

                <XAxis
                  dataKey="name"
                  interval={0}
                  angle={-28}
                  textAnchor="end"
                  height={80}
                  tick={{
                    fontSize: 9,
                  }}
                />

                <YAxis
                  tickFormatter={
                    formatBytes
                  }
                  tick={{
                    fontSize: 10,
                  }}
                />

                <Tooltip
                  formatter={(
                    value,
                  ) =>
                    formatBytes(
                      value,
                    )
                  }
                />

                <Bar
                  dataKey="total_bytes"
                  name="Usage"
                  fill={
                    chartColors[3]
                  }
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Paper>

        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            minHeight: 390,
            borderRadius: 0,
          }}
        >
          <SectionHeader
            title="Destination countries"
            subtitle="Countries receiving traffic generated by guest devices."
          />

          <Box
            sx={{
              mt: 1,
              height: 315,
            }}
          >
            <ResponsiveContainer
              width="100%"
              height="100%"
            >
              <BarChart
                data={
                  countryChartData
                }
                layout="vertical"
                margin={{
                  top: 5,
                  right: 25,
                  bottom: 5,
                  left: 5,
                }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#EAECF0"
                />

                <XAxis
                  type="number"
                  tickFormatter={
                    formatBytes
                  }
                  tick={{
                    fontSize: 10,
                  }}
                />

                <YAxis
                  type="category"
                  dataKey="name"
                  width={65}
                  tick={{
                    fontSize: 10,
                  }}
                />

                <Tooltip
                  formatter={(
                    value,
                  ) =>
                    formatBytes(
                      value,
                    )
                  }
                />

                <Bar
                  dataKey="total_bytes"
                  name="Usage"
                  fill={
                    chartColors[4]
                  }
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
      </Box>

      {/* Shop table */}

      <Paper
        variant="outlined"
        sx={{
          mt: 1.2,
          borderRadius: 0,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            p: 1.2,

            borderBottom:
              "1px solid #E3E7EC",
          }}
        >
          <SectionHeader
            title="Shop comparison"
            subtitle="Detailed usage, vouchers, devices and contribution by shop."
          />
        </Box>

        <SoftDataGrid
          rows={shopUsage}
          columns={shopColumns}
          loading={
            shopsUsageQuery.isFetching
          }
          getRowId={(row) =>
            row.shop?.id
          }
          pageSizeOptions={[
            10,
            20,
            50,
            100,
          ]}
          initialState={{
            pagination: {
              paginationModel: {
                page: 0,
                pageSize: 10,
              },
            },
          }}
        />
      </Paper>

      {/* Firewall cards */}

      <Paper
        variant="outlined"
        sx={{
          mt: 1.2,

          borderRadius: 0,

          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            p: 1.2,

            borderBottom:
              "1px solid #E3E7EC",
          }}
        >
          <SectionHeader
            title="Firewall traffic"
            subtitle="Traffic received from registered Sophos firewall infrastructure."
          />
        </Box>

        <Box
          sx={{
            display: "grid",

            gridTemplateColumns: {
              xs: "1fr",

              md: "1fr 1fr",

              xl:
                "repeat(3, 1fr)",
            },
          }}
        >
          {firewalls.length === 0 ? (
            <Box
              sx={{
                p: 2,
                gridColumn:
                  "1 / -1",
              }}
            >
              <Typography
                sx={{
                  color:
                    "#667085",

                  fontSize:
                    "0.7rem",
                }}
              >
                No firewall traffic
                found for the selected
                filters.
              </Typography>
            </Box>
          ) : (
            firewalls.map(
              (row) => (
                <Box
                  key={
                    row.firewall.id
                  }
                  sx={{
                    p: 1.5,

                    borderRight:
                      "1px solid #EAECF0",

                    borderBottom:
                      "1px solid #EAECF0",
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                  >
                    <RouterOutlinedIcon
                      sx={{
                        fontSize: 20,
                      }}
                    />

                    <Box>
                      <Typography
                        sx={{
                          fontSize:
                            "0.72rem",

                          fontWeight:
                            850,
                        }}
                      >
                        {row.firewall
                          .device_name ||
                          row.firewall
                            .device_serial_id}
                      </Typography>

                      <Typography
                        sx={{
                          color:
                            "#667085",

                          fontSize:
                            "0.61rem",
                        }}
                      >
                        {row.firewall
                          .device_model ||
                          "Unknown model"}
                      </Typography>
                    </Box>
                  </Stack>

                  <Typography
                    sx={{
                      mt: 1,

                      fontSize:
                        "1.1rem",

                      fontWeight:
                        900,
                    }}
                  >
                    {row.total?.human ||
                      "0 bytes"}
                  </Typography>

                  <Typography
                    sx={{
                      mt: 0.5,
                      color:
                        "#667085",
                      fontSize:
                        "0.61rem",
                    }}
                  >
                    Upload{" "}
                    {row.upload?.human ||
                      "0 bytes"}
                    {" • "}
                    Download{" "}
                    {row.download?.human ||
                      "0 bytes"}
                  </Typography>

                  <Typography
                    sx={{
                      mt: 0.25,
                      color:
                        "#667085",
                      fontSize:
                        "0.61rem",
                    }}
                  >
                    {row.shop_count} shops
                    {" • "}
                    {row.voucher_count} vouchers
                    {" • "}
                    {row.event_count} events
                  </Typography>
                </Box>
              ),
            )
          )}
        </Box>
      </Paper>

      {/* Events table */}

      <Paper
        variant="outlined"
        sx={{
          mt: 1.2,

          borderRadius: 0,

          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            p: 1.2,

            borderBottom:
              "1px solid #E3E7EC",
          }}
        >
          <SectionHeader
            title="Recent traffic events"
            subtitle="Latest individual traffic records attributed to guest vouchers."
          />
        </Box>

        <SoftDataGrid
          rows={events}
          columns={eventColumns}
          loading={
            eventsQuery.isFetching
          }
          getRowId={(row) =>
            row.id
          }
          pageSizeOptions={[
            10,
            20,
            50,
          ]}
          initialState={{
            pagination: {
              paginationModel: {
                page: 0,
                pageSize: 10,
              },
            },
          }}
        />
      </Paper>

      {/* Insights */}

      <Box
        sx={{
          display: "grid",

          gridTemplateColumns: {
            xs: "1fr",
            md:
              "repeat(3, 1fr)",
          },

          gap: 1.2,

          mt: 1.2,
        }}
      >
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            borderRadius: 0,
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
          >
            <PublicOutlinedIcon />

            <Box>
              <Typography
                sx={{
                  color:
                    "#667085",

                  fontSize:
                    "0.65rem",
                }}
              >
                Destination countries
              </Typography>

              <Typography
                sx={{
                  fontWeight:
                    900,

                  fontSize:
                    "1.15rem",
                }}
              >
                {countries.length}
              </Typography>
            </Box>
          </Stack>
        </Paper>

        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            borderRadius: 0,
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
          >
            <AppsRoundedIcon />

            <Box>
              <Typography
                sx={{
                  color:
                    "#667085",

                  fontSize:
                    "0.65rem",
                }}
              >
                Top application
              </Typography>

              <Typography
                sx={{
                  fontWeight:
                    900,

                  fontSize:
                    "1rem",
                }}
              >
                {applications[0]
                  ?.name ||
                  "—"}
              </Typography>

              <Typography
                sx={{
                  color:
                    "#667085",

                  fontSize:
                    "0.61rem",
                }}
              >
                {applications[0]
                  ?.total
                  ?.human ||
                  "0 bytes"}
              </Typography>
            </Box>
          </Stack>
        </Paper>

        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            borderRadius: 0,
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
          >
            <StorefrontOutlinedIcon />

            <Box>
              <Typography
                sx={{
                  color:
                    "#667085",

                  fontSize:
                    "0.65rem",
                }}
              >
                Highest usage shop
              </Typography>

              <Typography
                sx={{
                  fontWeight:
                    900,

                  fontSize:
                    "1rem",
                }}
              >
                {shopUsage[0]
                  ?.shop
                  ?.shop_name ||
                  "—"}
              </Typography>

              <Typography
                sx={{
                  color:
                    "#667085",

                  fontSize:
                    "0.61rem",
                }}
              >
                {shopUsage[0]
                  ?.total
                  ?.human ||
                  "0 bytes"}
              </Typography>
            </Box>
          </Stack>
        </Paper>
      </Box>

      <TrafficAnalyticsFiltersDrawer
        open={drawerOpen}
        onClose={() =>
          setDrawerOpen(false)
        }
        filters={filters}
        setFilters={setFilters}
        shops={shops}
        onApply={refreshAll}
        onReset={resetFilters}
      />
    </Box>
  );
}